import React, { useEffect, useState } from "react";
import api, { eur, STATUS_LABELS } from "../lib/api";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ClientsManager } from "../components/ClientsManager";
import { OrderManager } from "../components/OrderManager";
import { ProductManager } from "../components/ProductManager";
import { PricingManager } from "../components/PricingManager";
import { InvoicesManager } from "../components/InvoicesManager";
import { CommissionManager } from "../components/CommissionManager";
import { SecurityManager } from "../components/SecurityManager";
import { AccessLog } from "../components/AccessLog";
import { Euro, ShoppingBag, Users, TrendingUp, LayoutDashboard, ShieldCheck, Coins } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend,
} from "recharts";

const PIE_COLORS = ["hsl(21 100% 45%)", "hsl(197 37% 34%)", "hsl(43 74% 49%)", "hsl(123 46% 34%)", "hsl(280 40% 45%)", "hsl(123 46% 55%)", "hsl(0 72% 45%)"];

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="tabular mt-1 font-display text-3xl font-extrabold">{value}</p>
          {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${accent}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </Card>
  );
}

export default function AdminView() {
  const [stats, setStats] = useState(null);
  const [failedCount, setFailedCount] = useState(0);
  const loadStats = () => api.get("/stats").then((r) => setStats(r.data)).catch(() => {});

  const loadSecurityAlerts = () => {
    api.get("/access-logs").then((r) => {
      const lastSeen = Number(localStorage.getItem("security_last_seen") || 0);
      const n = r.data.filter((l) => !l.success && new Date(l.created_at).getTime() > lastSeen).length;
      setFailedCount(n);
    }).catch(() => {});
  };

  useEffect(() => { loadStats(); loadSecurityAlerts(); }, []);

  const markSecuritySeen = () => {
    localStorage.setItem("security_last_seen", String(Date.now()));
    setFailedCount(0);
  };

  if (!stats) return <p className="text-muted-foreground">Chargement...</p>;

  const statusData = Object.entries(stats.orders_by_status)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_LABELS[k], value: v }));

  return (
    <div data-testid="admin-view">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Direction du groupe</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Supervision globale</h1>
      </div>

      <Tabs defaultValue="dashboard" className="w-full" onValueChange={(v) => { if (v === "securite") markSecuritySeen(); }}>
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="gap-1.5">
            <LayoutDashboard className="h-4 w-4" /> Tableau de bord
          </TabsTrigger>
          <TabsTrigger value="securite" data-testid="tab-securite" className="gap-1.5">
            <ShieldCheck className="h-4 w-4" /> Sécurité
            {failedCount > 0 && (
              <span
                data-testid="security-alert-badge"
                title={`${failedCount} tentative(s) de connexion infructueuse(s)`}
                className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-bold leading-none text-white"
              >
                {failedCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="commission" data-testid="tab-commission" className="gap-1.5">
            <Coins className="h-4 w-4" /> Commission
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={Euro} label="Chiffre d'affaires" value={eur(stats.total_revenue)} accent="bg-primary/10 text-primary" />
            <StatCard icon={ShoppingBag} label="Commandes" value={stats.total_orders} accent="bg-secondary/15 text-secondary" />
            <StatCard icon={Users} label="Distributeurs" value={stats.total_clients} accent="bg-amber-500/10 text-amber-600" />
            <StatCard icon={TrendingUp} label="Taux de livraison" value={`${stats.delivery_rate}%`} accent="bg-primary/10 text-primary" />
          </div>

          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Card className="p-5 lg:col-span-2">
              <h3 className="mb-4 font-display font-bold">Chiffre d'affaires par distributeur</h3>
              {stats.revenue_by_client.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune donnée.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={stats.revenue_by_client} margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(0 0% 88%)" />
                    <XAxis dataKey="client" tick={{ fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => eur(v)} />
                    <Bar dataKey="revenue" fill="hsl(36 96% 50%)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card className="p-5">
              <h3 className="mb-4 font-display font-bold">Répartition des statuts</h3>
              {statusData.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune donnée.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={80}>
                      {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          <OrderManager onChange={loadStats} />

          <ProductManager />

          <PricingManager />

          <InvoicesManager />

          <Card className="mt-6 overflow-hidden">
            <div className="border-b border-border p-4"><h3 className="font-display font-bold">Distributeurs (comptas indépendantes)</h3></div>
            <Table>
              <TableHeader>
                <TableRow><TableHead>Distributeur</TableHead><TableHead>Commandes</TableHead><TableHead className="text-right">CA</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {stats.revenue_by_client.map((c) => (
                  <TableRow key={c.client} data-testid={`admin-client-${c.client}`}>
                    <TableCell className="font-medium">{c.client}</TableCell>
                    <TableCell className="tabular">{c.orders}</TableCell>
                    <TableCell className="tabular text-right font-semibold">{eur(c.revenue)}</TableCell>
                  </TableRow>
                ))}
                {stats.revenue_by_client.length === 0 && (
                  <TableRow><TableCell colSpan={3} className="py-6 text-center text-muted-foreground">Aucune commande.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>

          <ClientsManager />
        </TabsContent>

        <TabsContent value="securite" className="mt-6 space-y-6">
          <SecurityManager />
          <AccessLog />
        </TabsContent>

        <TabsContent value="commission" className="mt-6">
          <CommissionManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}
